import { createEntityStore } from "@/core/factory/store"
import { Risk, RISK_ENTITY, createEmptyRisk, RiskFamily } from "@/features/risks/entities/risk/model/risk.types"
import {
  createFromJob,
  fetchPublicRiskHistory,
  fetchValidatedAndSet,
  fetchWithUnitsAndSet,
  suggestOne,
  suggestForUnit,
  updateValidated,
  fetchPublicAndSet,
  importRisks
} from "@/features/risks/entities/risk/model/risk.thunk"
import { createSelector } from "@reduxjs/toolkit"
import { CustomEntityState } from "@/core/factory/types"
import { Unit, UNIT_ENTITY } from "../../unit/model/unit.types"
import { formatRisk } from "@/features/risks/entities/risk/model/risk.formatter"
import { groupBy, orderBy, sum, uniq } from "lodash"
import { buildRiskAssociated } from "./risk.utils"
import { sortItems } from "@/core/utils/sort-items.utils"
import { Organization } from "@/features/adm/entities/organization/model/organization.types"
import organizationStore from "@/features/adm/entities/organization/model/organization.store"

export const createRiskStore = () => {
  const store = createEntityStore<Risk, Organization>(
    RISK_ENTITY,
    createEmptyRisk,
    organizationStore.selectors.active,
    formatRisk
  )

  /**
   * Retourne la liste des first attributes de risques (champ calculé `name`) sans doublons.
   * Utile pour alimenter un select ou afficher un regroupement.
   */
  const listNames = createSelector(store.selectors.list, (risks) => {
    const names = risks.map((r) => r.name).filter(Boolean)
    return Array.from(new Set(names))
  })

  /**
   * For RiskValidated widget
   * List all current risks selected by units
   *
   * ⚠️ IMPORTANT: buildRiskAssociated must run on ALL risks with same signature
   * BEFORE filtering by unit, otherwise associatedRisks won't be found
   */
  const listSelectedByUnits = createSelector(
    [
      store.selectors.list,
      store.selectors.sort,
      (s) => s[UNIT_ENTITY] as CustomEntityState<Unit>,
      (s) => (s[RISK_ENTITY] as CustomEntityState<Risk>).filter,
      (s) => (organizationStore.selectors.active(s) as Organization | null)?.optionalRiskAttributes ?? [] as string[]
    ],
    (risks, sort, unitSlice, filter, optionalRiskAttributes) => {
      const { selected, list: units } = unitSlice

      // --- 1) Index pour lookup O(1)
      const unitById = new Map(units.map((u) => [u.id, u]))

      // --- 2) Grouper par name et enrichir avec associatedRisks AVANT de filtrer par unit
      const familiesByName: Record<string, Risk[]> = {}

      for (const risk of risks) {
        const cleanedName = typeof risk.name === "string" ? risk.name.trim() : risk.name
        if (!familiesByName[cleanedName]) {
          familiesByName[cleanedName] = []
        }
        familiesByName[cleanedName].push(risk)
      }

      // --- 3) Enrichir chaque famille avec les associatedRisks avant filtrage
      const enrichedByName: Record<string, Risk[]> = {}
      for (const [cleanedName, familyRisks] of Object.entries(familiesByName)) {
        enrichedByName[cleanedName] = buildRiskAssociated(familyRisks)
      }

      // --- 4) Maintenant filtrer par unit sélectionnée ET par RPS/PHY
      const filteredRisks: Risk[] = []

      // Si filtre incomplete est activé, identifier les familles qui ont au moins une fiche incomplète
      const familiesWithIncomplete = new Set<string>()
      if (filter?.incomplete === true) {
        for (const [familyName, familyRisks] of Object.entries(enrichedByName)) {
          const hasIncomplete = familyRisks.some((risk) =>
            Object.entries(risk.attributes ?? {}).some(([key, value]: any) =>
              !optionalRiskAttributes.includes(key) && (!value || (typeof value === 'string' && value.trim() === ""))
            )
          )
          if (hasIncomplete) {
            familiesWithIncomplete.add(familyName)
          }
        }
      }

      for (const [familyName, familyRisks] of Object.entries(enrichedByName)) {
        for (const risk of familyRisks) {
          // Doit être dans une UT sélectionnée OU avoir un associatedRisks dans une UT sélectionnée
          const isInSelectedUnit = selected?.length && risk.UnitId ? selected.includes(risk.UnitId) : !selected?.length

          const hasAssocInSelectedUnit =
            risk.associatedRisks?.some((ar) =>
              selected?.length && ar.UnitId ? selected.includes(ar.UnitId) : !selected?.length
            ) ?? false

          if (isInSelectedUnit || hasAssocInSelectedUnit) {
            // Appliquer les filtres RPS/PHY et fiches à compléter
            let passRpsFilter = true
            if (filter?.rps !== undefined && filter?.rps !== null) {
              passRpsFilter = risk.rps === filter.rps
            }

            // Pour incomplete: si la famille a une fiche incomplète, inclure TOUS les risques de la famille
            let passIncompleteFilter = true
            if (filter?.incomplete === true) {
              passIncompleteFilter = familiesWithIncomplete.has(familyName)
            }

            if (passRpsFilter && passIncompleteFilter) {
              filteredRisks.push(risk)
            }
          }
        }
      }

      // --- 5) Enrichir avec unitSize et préparer pour affichage
      let enrichedRisks = filteredRisks.map((risk) => {
        const unit = risk.UnitId ? unitById.get(risk.UnitId) : undefined

        const cleanedName = typeof risk.name === "string" ? risk.name.trim() : risk.name

        const cleanedAttributes = Object.fromEntries(
          Object.entries(risk.attributes ?? {}).map(([key, value]) => [
            key,
            typeof value === "string" ? value.trim() : value
          ])
        )

        return {
          ...risk,
          unitSize: unit?.size ?? null,
          _cleanedName: cleanedName,
          _cleanedAttributes: cleanedAttributes,
          associatedRisks: (risk.associatedRisks ?? []).map((ar) => ({
            ...ar,
            unitSize: unitById.get(ar.UnitId ?? "")?.size ?? null
          }))
        }
      })

      // --- 6) Grouper par name et trier
      enrichedRisks = orderBy(enrichedRisks, [(r) => r.unitSize ?? -Infinity, "valueEffective"], ["desc", "desc"])

      // --- 7) Construire les familles finales
      const families: RiskFamily[] = []

      for (const [cleanedName, groupRisks] of Object.entries(groupBy(enrichedRisks, "_cleanedName"))) {
        const uniqueUnitIds = uniq(groupRisks.map((r) => r.UnitId).filter((id): id is string => !!id))
        const size = sum(uniqueUnitIds.map((id) => unitById.get(id)?.size ?? 0))

        const maxDateValidated = groupRisks.reduce((max, current) => {
          if (!current.dateValidated) return max
          return !max || new Date(current.dateValidated) > new Date(max) ? current.dateValidated : max
        }, null as any)

        // Trier les risques dans la famille selon le sort et le filtre
        let sortedRisks = groupRisks

        if (filter?.incomplete === true) {
          // Si filtre incomplete, mettre les fiches incomplètes en premier
          sortedRisks = orderBy(sortedRisks, [
            (r) => {
              const isIncomplete = Object.entries(r.attributes ?? {}).some(([key, value]: any) =>
                !optionalRiskAttributes.includes(key) && (!value || (typeof value === 'string' && value.trim() === ""))
              )
              return isIncomplete ? 0 : 1
            }
          ])
        }

        if (sort.key === "dateValidated") {
          // Trier par date validée (plus récent en premier)
          sortedRisks = orderBy(sortedRisks, [(r) => r.dateValidated ? new Date(r.dateValidated).getTime() : 0], ["desc"])
        }

        families.push({
          id: groupRisks.map((r) => r.id).join("_"),
          name: cleanedName,
          Risks: sortedRisks,
          riskMax: orderBy(groupRisks, "valueEffective", "desc")[0],
          size,
          dateValidated: maxDateValidated
        } as any)
      }

      // --- 8) Tri final
      return sortItems(families, sort)
    }
  )

  /**
   * Memoized grouping of risks by signature (attributes + frequency + severity + prevention)
   * Avoids re-grouping on every selector call
   */
  const risksBySignature = createSelector(store.selectors.list, (risks) => {
    const grouped: Record<string, Risk[]> = {}
    for (const risk of risks) {
      const sig = JSON.stringify({
        attributes: risk.attributes,
        frequency: risk.frequency,
        prevention: risk.prevention,
        severity: risk.severity
      })
      if (!grouped[sig]) grouped[sig] = []
      grouped[sig].push(risk)
    }
    return grouped
  })

  /**
   * Enrich draft with associatedRisks from the full dataset
   * Ensures associatedRisks is populated on initial load
   * Preserves them during editing to avoid loss when attributes change
   */
  const draftWithAssoc = createSelector([store.selectors.draft, risksBySignature], (draft, grouped) => {
    if (!draft?.id) return draft

    // Only add associatedRisks if draft doesn't have them
    // This ensures they're preserved during editing and not recalculated
    if (draft.associatedRisks && draft.associatedRisks.length > 0) {
      return draft
    }

    // Add associatedRisks only if empty
    const sig = JSON.stringify({
      attributes: draft.attributes,
      frequency: draft.frequency,
      prevention: draft.prevention,
      severity: draft.severity
    })

    const group = grouped[sig] ?? []
    return {
      ...draft,
      associatedRisks: group.filter((r) => r.id !== draft.id)
    }
  })

  return {
    ...store,
    selectors: {
      ...store.selectors,
      listNames,
      listSelectedByUnits,
      draft: draftWithAssoc
    },
    thunks: {
      ...store.thunks,
      createFromJob,
      fetchPublicAndSet,
      fetchPublicRiskHistory,
      fetchValidatedAndSet,
      fetchWithUnitsAndSet,
      suggestOne,
      suggestForUnit,
      updateValidated,
      importRisks
    }
  }
}

const riskStore = createRiskStore()
export default riskStore
